import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { Building2, Building, Grid3X3, List, MapPin, Home, Eye, LogIn, ArrowUpDown, ArrowLeftRight, FilterIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { fetchEdificios } from '@/pages/parametrizacion/edificios/Services/edificios.services';
import { fetchHospedajesByEdificio } from '@/services/hospedajes.service';
import FilterDrawer from '@/components/FilterDrawer';
import AppliedFilters from '@/components/AppliedFilters';
import PropertiesTable from '@/pages/components/PropertiesTable';
import HospedajesTable from '@/pages/components/HospedajesTable';



const PropertiesPage = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const { edificioId } = useParams(); // Obtener edificioId de la URL
  const [buildings, setBuildings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'list'
  const [filters, setFilters] = useState();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  
  // Estados para vista de hospedajes
  const [currentView, setCurrentView] = useState('properties'); // 'properties' or 'hospedajes'
  const [selectedBuilding, setSelectedBuilding] = useState(null);
  const [hospedajes, setHospedajes] = useState([]);
  const [hospedajesLoading, setHospedajesLoading] = useState(false);

  const fetchAndSetBuildings = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await fetchEdificios({ 
        limit: 1000, // Cargar todos los edificios
        offset: 1, 
        query: filters,
        userId: user?.id, // Usar 'id' del usuario normalizado
        userRole: user?.role
      });
      setBuildings(data);
    } catch (error) {
      console.error('Error al obtener edificios:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar las propiedades. Por favor, intenta de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [filters, user, toast]);

  useEffect(() => {
    fetchAndSetBuildings();
  }, [fetchAndSetBuildings]);

  // Función para manejar la vista de hospedajes
  const handleManageHospedajes = useCallback(async (building) => {
    setSelectedBuilding(building);
    setCurrentView('hospedajes');
    setHospedajesLoading(true);
    
    try {
      const { data } = await fetchHospedajesByEdificio(building._id, {
        limit: 100,
        offset: 1,
        query: {}
      });
      setHospedajes(data || []);
    } catch (error) {
      console.error('Error al cargar hospedajes:', error);
      toast({
        title: "Error",
        description: "No se pudieron cargar los hospedajes. Por favor, intenta de nuevo.",
        variant: "destructive",
      });
      setHospedajes([]);
    } finally {
      setHospedajesLoading(false);
    }
  }, [toast]);

  // Detectar si hay edificioId en la URL y cargar hospedajes automáticamente
  useEffect(() => {
    if (edificioId && buildings.length > 0) {
      const edificio = buildings.find(b => 
        b._id === edificioId || 
        b.id === edificioId
      );
      
      if (edificio) {
        handleManageHospedajes(edificio);
      }
    }
  }, [edificioId, buildings, handleManageHospedajes]);

  // Separar edificios por tipo de logística
  const edificiosVertical = useMemo(() => {
    return buildings.filter(b => b.tipoLogistica === 'vertical');
  }, [buildings]);

  const edificiosHorizontal = useMemo(() => {
    return buildings.filter(b => b.tipoLogistica === 'horizontal');
  }, [buildings]);

  // Agrupar edificios horizontales por zona
  const edificiosHorizontalPorZona = useMemo(() => {
    const grupos = {};
    edificiosHorizontal.forEach(edificio => {
      const zona = edificio.zona || 'Sin zona';
      if (!grupos[zona]) {
        grupos[zona] = [];
      }
      grupos[zona].push(edificio);
    });
    return grupos;
  }, [edificiosHorizontal]);

  const edificiosMixto = useMemo(() => {
    return buildings.filter(b => b.tipoLogistica === 'mixto');
  }, [buildings]);

  // Campos de filtro para el FilterDrawer
  const FILTER_FIELDS = useMemo(() => [
    {
      id: 'nombre',
      label: 'Nombre del Edificio',
      type: 'text',
      placeholder: 'Buscar por nombre...'
    },
    {
      id: 'ciudad',
      label: 'Ciudad',
      type: 'text',
      placeholder: 'Buscar por ciudad...'
    },
    {
      id: 'tipo',
      label: 'Tipo de Edificio',
      type: 'select',
      options: [
        { value: 'casa', label: 'Casa(s)' },
        { value: 'apto', label: 'Apartamento(s)' },
        { value: 'otro', label: 'Otro' },
      ]
    },
    {
      id: 'tipoLogistica',
      label: 'Tipo Logística',
      type: 'select',
      options: [
        { value: 'horizontal', label: 'Horizontal' },
        { value: 'vertical', label: 'Vertical' },
        { value: 'mixto', label: 'Mixto' },
      ]
    },
  ], []);

  const handleRemoveFilter = (filterKey) => {
    const newFilters = { ...filters };
    delete newFilters[filterKey];
    setFilters(newFilters);
  };

  const getTypeIcon = (tipo) => {
    const tipoStr = String(tipo || '').toLowerCase();
    switch (tipoStr) {
      case 'casa':
        return <Home className="h-4 w-4" />;
      case 'apto':
      case 'apartamento':
        return <Building className="h-4 w-4" />;
      default:
        return <Building className="h-4 w-4" />;
    }
  };

  const getTypeLabel = (tipo) => {
    const tipoStr = String(tipo || '').toLowerCase();
    switch (tipoStr) {
      case 'casa':
        return 'Casa';
      case 'apto':
        return 'Apartamento';
      default:
        return tipo || 'Otro';
    }
  };

  const getStatusBadge = (estado) => {
    const variant = estado === 'Activo' ? 'default' : 'secondary';
    return (
      <Badge variant={variant} className="text-xs">
        {estado}
      </Badge>
    );
  };

  const handleBackToProperties = () => {
    setCurrentView('properties');
    setSelectedBuilding(null);
    setHospedajes([]);
  };

  // Handlers para acciones de hospedajes
  const handleViewHospedaje = (hospedaje) => {
    console.log('Ver hospedaje:', hospedaje);
    toast({
      title: "Funcionalidad en desarrollo",
      description: `Ver detalles de ${hospedaje.nombrePropiedad}`,
    });
  };

  const handleEditHospedaje = (hospedaje) => {
    console.log('Editar hospedaje:', hospedaje);
    toast({
      title: "Funcionalidad en desarrollo",
      description: `Editar ${hospedaje.nombrePropiedad}`,
    });
  };

  const handleDeleteHospedaje = (hospedaje) => {
    console.log('Eliminar hospedaje:', hospedaje);
    toast({
      title: "Funcionalidad en desarrollo",
      description: `Eliminar ${hospedaje.nombrePropiedad}`,
      variant: "destructive",
    });
  };

  const CardView = ({ buildings: buildingsToShow, sectionTitle, icon: Icon }) => (
    <div className="space-y-4">
      {buildingsToShow.length > 0 && (
        <div className="flex items-center space-x-2 mb-4">
          <Icon className="h-5 w-5 text-primary" />
          <h2 className="text-xl font-bold">{sectionTitle}</h2>
          <Badge variant="outline" className="ml-2">{buildingsToShow.length}</Badge>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <AnimatePresence>
          {buildingsToShow.map((building, index) => (
          <motion.div
            key={building._id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
          >
            <Card className="hover:shadow-lg transition-shadow duration-300 h-full">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-2">
                    {getTypeIcon(building.tipo)}
                    <CardTitle className="text-lg font-semibold truncate">
                      {building.nombre}
                    </CardTitle>
                  </div>
                  {/* {getStatusBadge(building.estado)} */}
                </div>
                <CardDescription className="text-sm">
                  {getTypeLabel(building.tipo)}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-medium text-foreground">{building.ciudad}</p>
                      <p className="truncate">{building.direccion}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div className="flex items-center space-x-1 text-sm">
                      <Building2 className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{building.numeroHospedajes || 0}</span>
                      <span className="text-muted-foreground">activos</span>
                    </div>
                    
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleManageHospedajes(building)}
                      className="h-8 w-8 p-0 hover:bg-primary hover:text-primary-foreground"
                      title="Gestionar activos"
                    >
                      <LogIn className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </AnimatePresence>
      </div>
    </div>
  );

  const ListView = ({ buildings: buildingsToShow, sectionTitle, icon: Icon }) => (
    <div className="space-y-4">
      {buildingsToShow.length > 0 && (
        <div className="flex items-center space-x-2 mb-4">
          <Icon className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">{sectionTitle}</h3>
          <Badge variant="outline" className="ml-2">{buildingsToShow.length}</Badge>
        </div>
      )}
      <PropertiesTable
        buildings={buildingsToShow}
        isLoading={isLoading}
        onManageHospedajes={handleManageHospedajes}
      />
    </div>
  );

  if (isLoading && buildings.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 animate-pulse" />
          <p className="text-muted-foreground">Cargando propiedades...</p>
        </div>
      </div>
    );
  }

  // Si estamos en la vista de hospedajes, mostrar HospedajesTable
  if (currentView === 'hospedajes') {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-6"
      >
        <HospedajesTable
          hospedajes={hospedajes}
          edificio={selectedBuilding}
          isLoading={hospedajesLoading}
          onView={handleViewHospedaje}
          onEdit={handleEditHospedaje}
          onDelete={handleDeleteHospedaje}
          onBack={handleBackToProperties}
        />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className=""
    >
      {/* Header */}
      <Card className="card-gradient-bg shadow-lg mb-4">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Building2 className="h-8 w-8 text-foreground" />
              <div>
                <CardTitle className="text-2xl font-bold text-foreground">
                  Portafolios
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Explora todos los portafolios y activos
                </CardDescription>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                onClick={() => setIsDrawerOpen(true)}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <FilterIcon className="h-4 w-4" />
                <span>Filtros</span>
              </Button>
              
              <div className="flex items-center border rounded-md">
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className="rounded-r-none"
                >
                  <Grid3X3 className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="rounded-l-none"
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Applied Filters */}
      <AppliedFilters
        filters={filters}
        fields={FILTER_FIELDS}
        onRemoveFilter={handleRemoveFilter}
      />

      {/* Content */}
      <div className="space-y-6">
        {buildings.length === 0 && !isLoading ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Building2 className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium text-muted-foreground mb-2">
                No se encontraron propiedades
              </p>
              <p className="text-sm text-muted-foreground text-center">
                Ajusta los filtros o verifica que existan propiedades en el sistema
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Logística Vertical */}
            {edificiosVertical.length > 0 && (
              <Card>
                <CardContent className="pt-6">
                  {viewMode === 'cards' ? (
                    <CardView 
                      buildings={edificiosVertical} 
                      sectionTitle="Logística Vertical" 
                      icon={ArrowUpDown}
                    />
                  ) : (
                    <ListView 
                      buildings={edificiosVertical} 
                      sectionTitle="Logística Vertical" 
                      icon={ArrowUpDown}
                    />
                  )}
                </CardContent>
              </Card>
            )}

            {/* Logística Horizontal - Agrupada por Zona */}
            {edificiosHorizontal.length > 0 && (
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-8">
                    {/* Header principal de Logística Horizontal */}
                    <div className="flex items-center space-x-2 mb-6">
                      <ArrowLeftRight className="h-6 w-6 text-primary" />
                      <h2 className="text-xl font-bold">Logística Horizontal</h2>
                      <Badge variant="outline" className="ml-2">{edificiosHorizontal.length}</Badge>
                    </div>

                    {/* Grupos por zona */}
                    {Object.entries(edificiosHorizontalPorZona).map(([zona, edificios]) => (
                      <div key={zona} className="space-y-4">
                        {viewMode === 'cards' ? (
                          <CardView 
                            buildings={edificios} 
                            sectionTitle={zona} 
                            icon={MapPin}
                          />
                        ) : (
                          <ListView 
                            buildings={edificios} 
                            sectionTitle={zona} 
                            icon={MapPin}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Logística Mixta */}
            {edificiosMixto.length > 0 && (
              <Card>
                <CardContent className="pt-6">
                  {viewMode === 'cards' ? (
                    <CardView 
                      buildings={edificiosMixto} 
                      sectionTitle="Logística Mixta" 
                      icon={Building2}
                    />
                  ) : (
                    <ListView 
                      buildings={edificiosMixto} 
                      sectionTitle="Logística Mixta" 
                      icon={Building2}
                    />
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fields={FILTER_FIELDS}
        onApply={(appliedFilters) => {
          setFilters(appliedFilters);
        }}
      />
    </motion.div>
  );
};

export default PropertiesPage;
